import { useState, useEffect } from 'react';
import { getHoldings } from '../../utils/api';
import { fmt } from '../../utils/format';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export default function HoldingsTab({ symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getHoldings(symbol).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [symbol]);

  if (loading) return <div style={{ padding: 24 }}>{[1,2,3].map(i => <div key={i} className="skeleton card" style={{ height: 80, marginBottom: 12 }} />)}</div>;
  if (!data) return <div className="error-msg">Unable to load holdings data.</div>;

  const top10 = (data.holdings || []).slice(0, 10);
  const COLORS = ['#3b82f6','#6366f1','#8b5cf6','#a855f7','#ec4899','#f43f5e','#f97316','#eab308','#22c55e','#06b6d4'];

  return (
    <div>
      {/* Key Stats */}
      <div className="card">
        <div className="card-title">Fund Statistics</div>
        <div className="grid-4">
          {data.dividendYield != null && (
            <div className="stat-cell"><div className="stat-label">Dividend Yield</div><div className="stat-value" style={{ color: 'var(--green)' }}>{fmt.pct(data.dividendYield * 100, 2)}</div></div>
          )}
          {data.expenseRatio != null && (
            <div className="stat-cell"><div className="stat-label">Expense Ratio</div><div className="stat-value">{fmt.pct(data.expenseRatio * 100, 2)}</div></div>
          )}
          {data.equityHoldings?.priceToEarnings != null && (
            <div className="stat-cell"><div className="stat-label">P/E Ratio</div><div className="stat-value">{(data.equityHoldings.priceToEarnings * 100).toFixed(1)}x</div></div>
          )}
          {data.equityHoldings?.priceToBook != null && (
            <div className="stat-cell"><div className="stat-label">P/B Ratio</div><div className="stat-value">{(data.equityHoldings.priceToBook * 100).toFixed(1)}x</div></div>
          )}
        </div>
      </div>

      {/* Top Holdings Bar Chart */}
      {top10.length > 0 && (
        <div className="card">
          <div className="card-title">Top {top10.length} Holdings</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={top10} layout="vertical" margin={{ left: 80, right: 60, top: 4, bottom: 4 }}>
              <XAxis type="number" tickFormatter={v => `${(v * 100).toFixed(1)}%`} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
              <YAxis type="category" dataKey="symbol" tick={{ fill: 'var(--text-primary)', fontSize: 12, fontFamily: 'JetBrains Mono' }} width={72} />
              <Tooltip
                formatter={(v) => [`${(v * 100).toFixed(2)}%`, 'Weight']}
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }}
              />
              <Bar dataKey="percent" radius={[0, 4, 4, 0]}>
                {top10.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Holdings Table */}
      {top10.length > 0 && (
        <div className="card">
          <div className="card-title">Holdings Detail</div>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>#</th>
                <th style={{ textAlign: 'left' }}>Symbol</th>
                <th style={{ textAlign: 'left' }}>Name</th>
                <th>Weight</th>
              </tr>
            </thead>
            <tbody>
              {top10.map((h, i) => (
                <tr key={h.symbol}>
                  <td style={{ color: 'var(--text-muted)', fontFamily: 'inherit' }}>{i + 1}</td>
                  <td style={{ textAlign: 'left', fontFamily: 'JetBrains Mono', fontWeight: 600, color: 'var(--accent)' }}>{h.symbol}</td>
                  <td style={{ textAlign: 'left', fontFamily: 'inherit', color: 'var(--text-secondary)' }}>{h.name}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                      <div style={{ width: 80, height: 6, background: 'var(--bg-surface)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${(h.percent / top10[0].percent) * 100}%`, height: '100%', background: COLORS[i % COLORS.length], borderRadius: 3 }} />
                      </div>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: 13 }}>{(h.percent * 100).toFixed(2)}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sector Weightings */}
      {data.sectorWeightings?.length > 0 && (
        <div className="card">
          <div className="card-title">Sector Allocation</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.sectorWeightings
              .flatMap(s => Object.entries(s))
              .filter(([, v]) => v > 0)
              .sort(([, a], [, b]) => b - a)
              .map(([sector, weight], i) => (
                <div key={sector} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 140, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right', flexShrink: 0 }}>
                    {sector.replace(/([A-Z])/g, ' $1').trim()}
                  </div>
                  <div style={{ flex: 1, height: 8, background: 'var(--bg-surface)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${weight * 100}%`, height: '100%', background: COLORS[i % COLORS.length], borderRadius: 4, transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ width: 48, fontSize: 12, fontFamily: 'JetBrains Mono', color: 'var(--text-primary)', flexShrink: 0 }}>
                    {(weight * 100).toFixed(1)}%
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
